/**
 * Email Analyzer Test Scenarios
 * 
 * This file contains predefined test scenarios for verifying the functionality 
 * of the email content analyzer feature. These scenarios cover various edge cases
 * and common patterns that email analyzers should detect.
 */

export interface EmailTestScenario {
  name: string;
  description: string;
  subject: string;
  body: string;
  expectedResults: {
    spamRiskCategory: 'low' | 'medium' | 'high';
    promoLikelihoodCategory: 'low' | 'medium' | 'high';
    expectedIssueCategories: string[];
  };
}

export interface ErrorTestScenario {
  name: string;
  description: string;
  errorType: 'config' | 'processing';
  expectedErrorMessage: string;
}

/**
 * Test scenarios that cover various aspects of email analysis
 */
export const TEST_SCENARIOS: EmailTestScenario[] = [
  {
    name: "Spam Keyword Test",
    description: "Email with high spam risk keywords and triggering patterns",
    subject: "URGENT: You've WON $5,000 - Act Fast!!!",
    body: `CONGRATULATIONS! You have been SELECTED as our LUCKY WINNER for our exclusive $5,000 CASH prize!

    🎯 ACT NOW - This offer expires in 24 HOURS!
    
    Simply click here to claim your GUARANTEED prize money: www.claim-your-prize-now.com
    
    No purchase necessary, but those who ORDER our premium package get DOUBLE the winnings!
    
    This is a LIMITED TIME OFFER! Don't miss your CHANCE!
    
    100% LEGITIMATE - This is NOT a scam!`,
    expectedResults: {
      spamRiskCategory: 'high',
      promoLikelihoodCategory: 'high',
      expectedIssueCategories: ['Urgency & Pressure', 'Money & Finance', 'Formatting', 'Links', 'Overpromise & Exaggeration', 'Shady & Deceptive', 'Action & Engagement']
    }
  },
  {
    name: "Dollar Sign Test",
    description: "Email with various dollar sign patterns to test detection",
    subject: "Pricing update: $ changes to our plans",
    body: `Hello team,

    We're updating our pricing structure. Here are the new prices:

    Basic Plan: $9.99/month
    Pro Plan: $19.99/month
    Enterprise Plan: $$$ (Triple dollar sign)
    Custom Option: $$$$ (Four dollar signs)

    We're offering a special 20% discount if you upgrade before the end of the month.
    
    For just $ amounts, please see our website.
    
    Earn $100 bonus credit when you refer a friend!
    
    Let me know if you have any questions about the $ changes.`,
    expectedResults: {
      spamRiskCategory: 'medium',
      promoLikelihoodCategory: 'medium',
      expectedIssueCategories: ['Money & Finance', 'Incentives & Offers']
    }
  },
  {
    name: "Suspicious Formatting Test",
    description: "Email with formatting issues but minimal spam keywords",
    subject: "Important Project Update!!!",
    body: `TEAM ANNOUNCEMENT

    PLEASE READ THIS IMPORTANT MESSAGE!!!
    
    The project deadline has been EXTENDED by two weeks. Everyone MUST update their timelines accordingly.
    
    REMINDER: All status reports are DUE this Friday!!!
    
    Thank you,
    Management`,
    expectedResults: {
      spamRiskCategory: 'medium',
      promoLikelihoodCategory: 'low',
      expectedIssueCategories: ['Formatting', 'all_caps_words', 'excessive_exclamation_body']
    }
  },
  {
    name: "Multiple Links Test",
    description: "Email with multiple links and URL shorteners",
    subject: "Check out these resource links",
    body: `Hello team,

    I've compiled some useful resources for our upcoming project:
    
    Main documentation: https://docs.example.com/project-x
    Quick start guide: https://bit.ly/quick-start
    Video tutorial: https://tiny.url/vid-tut
    Community forum: https://community.example.com
    
    The best part is these resources will TRANSFORM your productivity and GUARANTEE project success!
    
    Please review all materials before our next meeting.
    
    Click here to confirm you've reviewed: https://forms.example.com/confirm`,
    expectedResults: {
      spamRiskCategory: 'medium',
      promoLikelihoodCategory: 'medium',
      expectedIssueCategories: ['Links', 'Overpromise & Exaggeration', 'Action & Engagement']
    }
  },
  {
    name: "Image Placeholders Test",
    description: "Email with multiple image placeholders",
    subject: "New product catalog with images",
    body: `Check out our latest products:

    [PLACE HOLDER IMAGE]
    Our flagship product with enhanced features.
    
    [PLACE HOLDER IMAGE]
    The economy model with great value.
    
    [PLACE HOLDER IMAGE]
    Limited edition version - while supplies last!
    
    Let me know if you'd like more information about any of these.`,
    expectedResults: {
      spamRiskCategory: 'low',
      promoLikelihoodCategory: 'medium',
      expectedIssueCategories: ['Image Heavy']
    }
  },
  {
    name: "Empty Inputs Test",
    description: "Test for validation of empty inputs",
    subject: "",
    body: "",
    expectedResults: {
      spamRiskCategory: 'low',
      promoLikelihoodCategory: 'low',
      expectedIssueCategories: []
    }
  },
  {
    name: "Clean Professional Email",
    description: "A clean email with professional content and no issues",
    subject: "Quarterly Business Review Meeting - March 2023",
    body: `Dear Team,

    I'd like to schedule our quarterly business review for March 15, 2023, at 2:00 PM in the main conference room.
    
    Agenda items:
    - Q1 performance overview
    - Budget planning for Q2
    - Department updates (5 minutes per department)
    - Open discussion
    
    Please come prepared with your department's highlights and challenges. If you need to add specific items to the agenda, please let me know by the end of this week.
    
    Looking forward to a productive meeting.
    
    Best regards,
    Sarah Johnson
    Director of Operations`,
    expectedResults: {
      spamRiskCategory: 'low',
      promoLikelihoodCategory: 'low',
      expectedIssueCategories: []
    }
  },
  {
    name: "Mixed Content Email",
    description: "Email with some promotional elements but not excessive",
    subject: "Team Update: New Product Release",
    body: `Hi everyone,

    I'm excited to announce that we're launching our new product next week. The team has been working hard, and the results are impressive.
    
    Key features:
    - Improved user interface
    - 30% faster performance
    - New reporting dashboard
    
    [PLACE HOLDER IMAGE]
    
    We're offering a special team preview this Friday at 3 PM in the demo room. Light refreshments will be provided.
    
    Please RSVP by Thursday: https://company-events.example.com/rsvp
    
    Looking forward to showing you what we've built!
    
    Regards,
    Product Team`,
    expectedResults: {
      spamRiskCategory: 'low',
      promoLikelihoodCategory: 'medium',
      expectedIssueCategories: ['Links', 'Image Heavy']
    }
  }
];

/**
 * Simulated system error scenarios
 */
export const ERROR_SCENARIOS: ErrorTestScenario[] = [
  {
    name: "Configuration Loading Error",
    description: "Simulates error while loading analyzer configuration",
    errorType: "config",
    expectedErrorMessage: "Unable to load analyzer rules. Please try again later."
  },
  {
    name: "Analysis Processing Error",
    description: "Simulates error during email analysis processing",
    errorType: "processing",
    expectedErrorMessage: "Error analyzing email content. Please try again."
  }
];

/**
 * Helper function to load a test scenario into the email analyzer form
 * 
 * @param scenarioName Name of the scenario to load
 * @returns The found scenario or null if not found
 */
export function loadTestScenario(scenarioName: string): EmailTestScenario | null {
  return TEST_SCENARIOS.find(scenario => scenario.name === scenarioName) || null;
} 